import React, { useState, useMemo } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { Button, Empty, Spin, Select, DatePicker } from 'antd-v5';
import { CalendarOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import StandardLayoutV2 from '../../../Layout/StandardLayoutV2';
import EventCard from './components/EventCard';
import EventLinksModal from './components/EventLinksModal';
import { useEvents } from '../../../../hooks/query/useEvents';
import { useMerchantStores } from '../../../../hooks/query/useStores';
import './styles.scss';

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;

const PastEvents = () => {
  const history = useHistory();
  const { events: eventsFlag } = useFlags();

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }

  // Filter state
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  // Event Links Modal state
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Pagination state (Load More)
  const [pastEventsLimit, setPastEventsLimit] = useState(8);

  // Fetch stores for filter dropdown
  const { data: storesData } = useMerchantStores({
    useQueryOptions: {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes to reduce refetching
    },
  });
  const stores = storesData?.results || storesData?.data || [];

  // Build query params for API
  const queryParams = useMemo(() => {
    const params = {
      offset: 0,
      limit: 100, // API max is 100, pagination handled client-side
    };
    if (selectedStoreIds.length > 0) {
      // API expects comma-separated string, not array
      params.storeIds = selectedStoreIds.join(',');
    }
    if (dateRange[0]) {
      params.fromDate = dateRange[0].format('YYYY-MM-DD');
    }
    if (dateRange[1]) {
      params.toDate = dateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [selectedStoreIds, dateRange]);

  // Fetch events with filters
  const {
    data: eventsData,
    isLoading,
    error,
  } = useEvents({
    query: queryParams,
    useQueryOptions: {
      staleTime: 30 * 1000, // Cache for 30 seconds
    },
  });

  const events = eventsData?.data || eventsData || [];

  // Filter to only past events, sorted by start date (descending - most recent first)
  const pastEvents = useMemo(() => {
    const now = dayjs();
    const past = [];

    events.forEach(event => {
      const eventEndDate = event.endDate;

      // Past: end date is before today
      if (eventEndDate && dayjs(eventEndDate).isBefore(now, 'day')) {
        past.push(event);
      }
    });

    // Sort by start date (descending) - most recent first
    const sortByStartDateDesc = (a, b) => {
      const startA = a.startDate;
      const startB = b.startDate;

      // Events without dates go to the end
      if (!startA && !startB) return 0;
      if (!startA) return 1;
      if (!startB) return -1;

      return dayjs(startB).diff(dayjs(startA));
    };

    return past.sort(sortByStartDateDesc);
  }, [events]);

  // Paginated events for display
  const displayedPastEvents = pastEvents.slice(0, pastEventsLimit);
  const hasMorePastEvents = pastEvents.length > pastEventsLimit;

  const handleEventClick = eventId => {
    history.push(`/Events/event-details/${eventId}`);
  };

  const handleLinksClick = event => {
    setSelectedEvent(event);
    setShowLinksModal(true);
  };

  const handleBackToEvents = () => {
    history.push('/Events');
  };

  const breadcrumb = [{ path: '/Events', title: 'Events' }, { title: 'Past Events' }];

  if (error) {
    return (
      <StandardLayoutV2
        title="Past Events"
        breadcrumb={breadcrumb}
        classProps="events-page"
        containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
      >
        <div className="events-error">
          <Empty description="Failed to load events. Please try again." />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title="Past Events"
      breadcrumb={breadcrumb}
      classProps="events-page"
      containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
    >
      {/* Header Actions */}
      <div className="events-header flex-row-spacebetween-nowrap m-btm-24">
        <div className="flex-row-nospacebetween-nowrap gap-16">
          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by Store"
            value={selectedStoreIds}
            onChange={setSelectedStoreIds}
            style={{ minWidth: 180 }}
            maxTagCount={1}
            maxTagPlaceholder={omittedValues => `+${omittedValues.length} more`}
          >
            {stores.map(store => (
              <Select.Option key={store.id} value={store.id}>
                {store.name}
              </Select.Option>
            ))}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={dates => setDateRange(dates || [null, null])}
            placeholder={['Start Date', 'End Date']}
            allowClear
            disabledDate={date => date.isAfter(dayjs(), 'day')}
          />
        </div>
        <Button icon={<CalendarOutlined />} onClick={handleBackToEvents}>
          View Current Events
        </Button>
      </div>

      {isLoading ? (
        <div className="events-loading flex-col-center">
          <Spin size="large" />
        </div>
      ) : (
        <div className="events-section">
          {pastEvents.length > 0 ? (
            <>
              <div className="events-grid">
                {displayedPastEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    isPast
                    onClick={() => handleEventClick(event.id)}
                    onLinksClick={handleLinksClick}
                  />
                ))}
              </div>
              {hasMorePastEvents && (
                <div className="events-load-more m-top-24">
                  <Button onClick={() => setPastEventsLimit(prev => prev + 8)}>
                    Load More ({pastEvents.length - pastEventsLimit} remaining)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Empty description="No past events" />
          )}
        </div>
      )}

      {/* Event Links Modal */}
      <EventLinksModal open={showLinksModal} onClose={() => setShowLinksModal(false)} event={selectedEvent} />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(PastEvents);
